import { useEffect, useRef } from 'react'
import { Icon, Kbd } from '@/shared/ui'
import s from './GlobalSearch.module.css'

/**
 * Поле поиска шапки (prototype/Prototype.dc.html:168-172): ввод и фокус по ⌘K/Ctrl+K.
 *
 * Выдачу результатов (`GET /search` → `SearchResults`, `openapi/components/schemas/
 * platform.yaml`) в Task 16 не подключаю: это не один из «четырёх поповеров» шапки из
 * проверочного списка координатора (компании, уведомления, профиль, свитчер сервисов —
 * поиска среди них нет), брифа с шагом под неё тоже нет, а сама выдача — самостоятельная
 * поверхность (группы по броням/клиентам/апартаментам), а не пара строк под полем. Реализация
 * оставлена будущей задаче; здесь — честный ввод без придуманной логики поверх.
 */
export function GlobalSearch() {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (!event.metaKey && !event.ctrlKey) return

      // `event.code` — ФИЗИЧЕСКАЯ клавиша, `event.key` — символ по ТЕКУЩЕЙ раскладке.
      //
      // Только `event.key === 'k'` здесь было бы багом ровно для той аудитории, ради которой
      // всё и делается: при русской раскладке (ЙЦУКЕН) та же физическая клавиша даёт `key: 'л'`,
      // и ⌘K/Ctrl+K не сработал бы у менеджера, который печатает по-русски, — то есть у всех.
      // Проверено живым прогоном: синтетический keydown `{ ctrlKey: true, key: 'л', code: 'KeyK' }`
      // фокусирует поле; до правки — не фокусировал.
      //
      // `code` первым, `key` вторым — не для подстраховки: `code` привязан к физической позиции
      // и на Dvorak/AZERTY укажет не на ту клавишу, а `key` в этом случае как раз даст верное 'k'.
      // Вместе они покрывают и кириллицу, и переставленные латинские раскладки.
      if (event.code === 'KeyK' || event.key.toLowerCase() === 'k') {
        event.preventDefault()
        inputRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <label className={s.root}>
      <Icon name="search" size={14} className={s.icon} />
      <input
        ref={inputRef}
        type="text"
        name="global-search"
        autoComplete="off"
        placeholder="Найти гостя, бронь, телефон…"
        // Имя поля задаётся явно, а не берётся из placeholder.
        //
        // По спецификации accname содержимое <label> перебивает placeholder — а единственный
        // текст внутри этого <label> — подсказка «⌘K» (иконка декоративная). Без aria-label
        // скринридер объявлял бы поле поиска как «⌘K»: проверено снимком дерева доступности,
        // именно так оно и было.
        aria-label="Найти гостя, бронь, телефон"
        aria-keyshortcuts="Meta+K Control+K"
        className={s.input}
        onKeyDown={(event) => {
          if (event.key === 'Escape') inputRef.current?.blur()
        }}
      />
      {/* Визуальная подсказка о хоткее. Для скринридера её несёт aria-keyshortcuts выше — */}
      {/* здесь она была бы вторым, невнятным прочтением того же самого. */}
      <Kbd aria-hidden="true">⌘K</Kbd>
    </label>
  )
}
